
import LottieWeb from "../services/LottieWeb.js"
import TypeformService from "../services/TypeformService"
import NavLink from "./NavLink"
import AppButton from "./AppButton"
import AppSection from "./AppSection"
import Article from "./Article"
import stubDirectives from "../mixins/stubDirectives"
import formatStoryblokAssetUrl from "../helpers/formatStoryblokAssetUrl"
import markRaw from "../helpers/markRaw"
import buttonProps from "../helpers/buttonProps"

import QuoteInput from "./QuoteInput"

export default {
  components: {
    AppSection,
    Article,
    NavLink,
    QuoteInput
  },
  mixins: [stubDirectives],
  props: {
    heading: {
      type: String,
      default: ""
    },
    isHeadingUppercase: {
      type: Boolean,
      default: false
    },
    isHeadingLarge: {
      type: Boolean,
      default: false
    },
    subHeading: {
      type: String,
      default: ""
    },
    copy: {
      type: [Object, String],
      default: null
    },
    variableSet: {
      type: Object,
      default: () => {
        return {}
      }
    },
    fontColor: {
      type: String,
      default: ""
    },
    backgroundColor: {
      type: String,
      default: ""
    },
    /**
      Asset for the background image.
      If background video is specified it will be used as a poster image
    */
    backgroundImage: {
      type: Object,
      default: null
    },
    responsiveBackgroundImage: {
      type: Array,
      default: null
    },
    backgroundVideo: {
      type: Object,
      default: null
    },
    /**
      Background video asset in WebM format (optional, but recommended)
    */
    backgroundVideoWebM: {
      type: Object,
      default: null
    },
    image: {
      type: Object,
      default: null
    },
    fallbackImage: {
      type: Object,
      default: null
    },
    animation: {
      type: Object,
      default: null
    },
    button: {
      type: Array,
      default: null
    },
    /**
      Defines if the column containing text
      should be placed on the left hand side
    */
    contentOnLeftSide: {
      type: Boolean,
      default: false
    },
    /**
      Applies fade-in transition to image and the content
      Transition is not applied to the animation
    */
    fadeInOnScroll: {
      type: Boolean,
      default: false
    },
    mediaLarge: {
      type: Boolean,
      default: false
    },
    mediaSmall: {
      type: Boolean,
      default: false
    },
    hideMediaOnMobile: {
      type: Boolean,
      default: false
    },
    noTopPaddingMobile: {
      type: Boolean,
      default: false
    },
    noBottomPaddingMobile: {
      type: Boolean,
      default: false
    },
    headingBottomPaddingMobile: {
      type: Boolean,
      default: false
    },
    mediaFullWidthMobile: {
      type: Boolean,
      default: false
    },
    centerContentMobile: {
      type: Boolean,
      default: false
    },
    isImageAlignedTop: {
      type: Boolean,
      default: false
    },
    isHeadingAtTop: {
      type: Boolean,
      default: false
    },
    isHeadingCentered: {
      type: Boolean,
      default: false
    },
    largeCopyFontMobile: {
      type: Boolean,
      default: false
    },
    deferMediaLoading: {
      type: Boolean,
      default: false
    },
    typeformId: {
      type: String,
      default: ""
    },
    mediaFillContainer: {
      type: Boolean,
      default: false
    },
    useNaturalImageHeight: {
      type: Boolean,
      default: false
    },
    quoteInput: {
      type: Array,
      default: () => []
    },
    userSignedIn: {
      type: Boolean,
      default: false
    },
    contentHeadingBackgroundColor: {
      type: String,
      default: null
    },
    smallHeading: {
      type: String,
      default: null
    },
    hideBackgroundOnMobileAndHideImageOnDesktop: {
      type: Boolean,
      default: null
    },
    sectionId: {
      type: String,
      default: ""
    },
  },
  data() {
    return {
      elementInView: false,
      canLoadVideo: !this.deferMediaLoading,
      animationPlayer: null,
      AppButton: markRaw(AppButton)
    }
  },
  computed: {
    buttonProps() {
      return buttonProps(this.button, this.userSignedIn)
    },
    hasMedia() {
      return this.hasAnimation || this.hasImage
    },
    hasAnimation() {
      return Boolean(this.assetUrl(this.animation))
    },
    hasImage() {
      return Boolean(this.assetUrl(this.image))
    },
    hasFallbackImage() {
      return Boolean(this.assetUrl(this.fallbackImage))
    },
    imageMimeType() {
      let extension = this.image.filename.split(".").pop()
      if (extension === "jpg") extension = "jpeg" // image/jpg is not a valid mime type
      return `image/${extension}`
    }
  },
  mounted() {
    this.loadAnimation()
    this.canLoadVideo = true
  },
  methods: {
    assetUrl(asset) {
      return asset && asset.filename
    },
    async loadAnimation() {
      if (this.hasAnimation) {
        const animationUrl = formatStoryblokAssetUrl(
          this.assetUrl(this.animation)
        )
        const lottie = await LottieWeb.getLottieLight()
        this.animationPlayer = lottie.loadAnimation({
          container: this.$refs.animation,
          render: "svg",
          loop: 0,
          autoplay: false,
          path: animationUrl
        })
      }
    },
    elementCameInToView(isVisible) {
      if (!isVisible || this.elementInView) {
        return
      }
      this.elementInView = true
      if (this.hasAnimation) {
        this.animationPlayer.play()
      }
    },
    async buttonClicked() {
      this.$emit("button-click")
      if (this.typeformId) {
        await TypeformService.openPopup(this.typeformId)
      }
    },
    handleValidPostcode(data) {
      const validPostcode = data.customerPostcode
      this.$emit("valid-postcode", validPostcode)
    }
  }
}
